import React from "react";
import CrudManager from "../../../../components/common/CrudManager";
import customersService from "../../../../services/legacy/customersService";
import countiesService from "../../../../services/legacy/countiesService";
import statesService from "../../../../services/legacy/statesService";
import countriesService from "../../../../services/legacy/countriesService";
import taxGroupHeadingService from "../../../../services/legacy/taxGroupHeadingService";
import commissionSalesGroupsService from "../../../../services/legacy/commissionSalesGroupsService";
import priceDiscGroupsService from "../../../../services/legacy/priceDiscGroupsService";
import deliveryModesService from "../../../../services/legacy/deliveryModesService";
import customerPaymentModesService from "../../../../services/legacy/customerPaymentModesService";
import paymentSchedulesService from "../../../../services/legacy/paymentSchedulesService";
import contactsService from "../../../../services/legacy/contactsService";
import { faFileInvoice } from "@fortawesome/free-solid-svg-icons";

// Joins that duplicate the header row once per joined child row (e.g. one row
// per sales line). When a value here is true, CrudManager groups those rows
// back into a single visual row by default (see table-config.yml
// joins.<key>.frontend.groupDuplicateRows). Users can still toggle this from
// the grid UI; their choice is persisted per-user and overrides this default.
const JOIN_ROW_GROUPING_DEFAULTS = {"salesLine": true};

const DEFAULT_GRID_ROW_STYLE_RULES = [{"hover_text": "Pedido sin confirmar", "style": {"backgroundColor": "#fd5c5a"}, "when": {"field": "documentstatus", "operator": "lt", "value": 3}}, {"hover_text": "Pedido sin orden de manipulacion", "style": {"backgroundColor": "#cfe2ff"}, "when": {"field": "idaxhasmanipulationorder", "operator": "eq", "value": false}}, {"hover_text": "Pedido sin listado de direccion (confirmado al abrirlo)", "style": {"backgroundColor": "#ffd59e"}, "when": {"field": "idaxhasdirectionreport", "operator": "eq", "value": false}}];

const defaultGridRowStyleResolver = (row) => {
  if (!row || typeof row !== "object") {
    return null;
  }

  const getRowFieldValue = (row, fieldName) => {
    if (!row || typeof row !== "object" || !fieldName) {
      return null;
    }
    if (Object.prototype.hasOwnProperty.call(row, fieldName)) {
      return row[fieldName];
    }
    const normalizedTarget = String(fieldName).replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    const rowKey = Object.keys(row).find(
      (key) => String(key).replace(/[^a-zA-Z0-9]/g, "").toLowerCase() === normalizedTarget,
    );
    return rowKey ? row[rowKey] : null;
  };

  const toFiniteNumber = (value) => {
    if (value === undefined || value === null || value === "") {
      return null;
    }
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const evaluate = (left, operator, right, tolerance = 0) => {
    const leftNumber = toFiniteNumber(left);
    const rightNumber = toFiniteNumber(right);
    const bothNumeric = leftNumber !== null && rightNumber !== null;
    switch (operator) {
      case "eq":
        if (bothNumeric) return Math.abs(leftNumber - rightNumber) <= 0.0001;
        return String(left ?? "") === String(right ?? "");
      case "ne":
        if (bothNumeric) return Math.abs(leftNumber - rightNumber) > 0.0001;
        return String(left ?? "") !== String(right ?? "");
      case "ne_present":
        if (left === undefined || left === null || left === "" || right === undefined || right === null || right === "") return false;
        if (bothNumeric) return Math.abs(leftNumber - rightNumber) > 0.0001;
        return String(left ?? "") !== String(right ?? "");
      case "blank_or_zero":
        return left === undefined || left === null || left === "" || (leftNumber !== null && Math.abs(leftNumber) <= 0.0001);
      case "outside_percent_tolerance":
        if (!bothNumeric || Math.abs(rightNumber) <= 0.0001) return false;
        return Math.abs(leftNumber - rightNumber) > Math.abs(rightNumber) * Number(tolerance || 0);
      case "gt":
        return Number(left) > Number(right);
      case "gte":
        return Number(left) >= Number(right);
      case "lt":
        return Number(left) < Number(right);
      case "lte":
        return Number(left) <= Number(right);
      case "in":
        return Array.isArray(right) && right.map((v) => String(v)).includes(String(left ?? ""));
      case "not_in":
        return Array.isArray(right) && !right.map((v) => String(v)).includes(String(left ?? ""));
      case "contains":
        return String(left ?? "").toLowerCase().includes(String(right ?? "").toLowerCase());
      case "starts_with":
        return String(left ?? "").toLowerCase().startsWith(String(right ?? "").toLowerCase());
      case "ends_with":
        return String(left ?? "").toLowerCase().endsWith(String(right ?? "").toLowerCase());
      default:
        return false;
    }
  };

  for (const rule of DEFAULT_GRID_ROW_STYLE_RULES) {
    const field = rule?.when?.field;
    const operator = rule?.when?.operator || "eq";
    const value = rule?.when?.value_field
      ? getRowFieldValue(row, rule.when.value_field)
      : rule?.when?.value;
    if (!field) {
      continue;
    }
    if (evaluate(getRowFieldValue(row, field), operator, value, rule?.when?.tolerance ?? rule?.tolerance)) {
      return rule?.hover_text
        ? { style: rule?.style || null, title: rule.hover_text }
        : rule?.style || null;
    }
  }

  return null;
};

const SalesOrdersPageGen = ({ service, transformColumns, crudConfigOverride }) => {
  const formTabs = [{"tabId": "TabHeaderOverview", "label": "axFormLayout.salestable.tabs.tabheaderoverview", "order": 1, "groups": [{"groupId": "TabHeaderOverview_MainFields", "label": "axFormLayout.salestable.groups.tabheaderoverview.tabheaderoverview_mainfields", "order": 1, "fields": ["salesId", "purchorderformnum", "custaccount", "invoiceaccount", "currencyCode", "projid"]}], "fields": []}, {"tabId": "TabHeaderSimple", "label": "axFormLayout.salestable.tabs.tabheadersimple", "order": 2, "groups": [{"groupId": "GroupHeader", "label": "axFormLayout.salestable.groups.tabheadersimple.groupheader", "order": 1, "fields": ["contactpersonid", "deliveryname", "deliveryaddress", "salestaker", "salespoolid", "languageid", "onetimecustomer", "documentstatus", "salestype", "sourcefilehash", "sourcefolderpath", "sourcefilename", "sourceemailfilename", "sourcejsonfilename"]}], "fields": []}, {"tabId": "TabHeaderGeneral", "label": "axFormLayout.salestable.tabs.tabheadergeneral", "order": 3, "groups": [{"groupId": "Baseinfo", "label": "axFormLayout.salestable.groups.tabheadergeneral.baseinfo", "order": 1, "fields": ["salesname"]}, {"groupId": "ContactInfo", "label": "axFormLayout.salestable.groups.tabheadergeneral.contactinfo", "order": 2, "fields": ["email"]}, {"groupId": "status", "label": "axFormLayout.salestable.groups.tabheadergeneral.status", "order": 4, "fields": ["deadline"]}, {"groupId": "PrintPackingLineDetails", "label": "axFormLayout.salestable.groups.tabheadergeneral.printpackinglinedetails", "order": 7, "fields": ["printlinedetails"]}], "fields": []}, {"tabId": "TabHeaderSetup", "label": "axFormLayout.salestable.tabs.tabheadersetup", "order": 4, "groups": [{"groupId": "Administration", "label": "axFormLayout.salestable.groups.tabheadersetup.administration", "order": 1, "fields": ["salesunitid", "salesresponsible", "salesoriginid"]}, {"groupId": "Inventory", "label": "axFormLayout.salestable.groups.tabheadersetup.inventory", "order": 2, "fields": ["inventlocationid"]}, {"groupId": "Posting", "label": "axFormLayout.salestable.groups.tabheadersetup.posting", "order": 3, "fields": ["postingprofile", "numbersequencegroup", "invoicingtypeid"]}, {"groupId": "SalesTax", "label": "axFormLayout.salestable.groups.tabheadersetup.salestax", "order": 4, "fields": ["taxgroup", "vatnum", "incltax"]}, {"groupId": "Reservation", "label": "axFormLayout.salestable.groups.tabheadersetup.reservation", "order": 5, "fields": ["reservation"]}, {"groupId": "Commission", "label": "axFormLayout.salestable.groups.tabheadersetup.commission", "order": 6, "fields": ["salesgroup"]}, {"groupId": "GiroMoneyTransferSlip", "label": "axFormLayout.salestable.groups.tabheadersetup.giromoneytransferslip", "order": 7, "fields": ["girotype"]}, {"groupId": "Albaran", "label": "axFormLayout.salestable.groups.tabheadersetup.albaran", "order": 9, "fields": ["albaranvalued"]}], "fields": []}, {"tabId": "TabHeaderAddress", "label": "axFormLayout.salestable.tabs.tabheaderaddress", "order": 5, "groups": [{"groupId": "HeaderDeliveryAddress", "label": "axFormLayout.salestable.groups.tabheaderaddress.headerdeliveryaddress", "order": 1, "fields": ["deliverystreet", "deliveryzipcode", "deliverycity", "deliverycounty", "deliverystate", "deliverycountryregionid", "deliveryphone"]}], "fields": []}, {"tabId": "TabHeaderDelivery", "label": "axFormLayout.salestable.tabs.tabheaderdelivery", "order": 6, "groups": [{"groupId": "Delivery", "label": "axFormLayout.salestable.groups.tabheaderdelivery.delivery", "order": 1, "fields": ["requesteddelivdateorig", "requesteddelivdate", "warehousetimecalendar", "dlvmode", "dlvreason", "exportreason", "cmturgencykind"]}, {"groupId": "Transport", "label": "axFormLayout.salestable.groups.tabheaderdelivery.transport", "order": 2, "fields": ["freightzone", "freightsliptype"]}], "fields": []}, {"tabId": "TabHeaderPriceCalc", "label": "axFormLayout.salestable.tabs.tabheaderpricecalc", "order": 7, "groups": [{"groupId": "Payment", "label": "axFormLayout.salestable.groups.tabheaderpricecalc.payment", "order": 2, "fields": ["payment", "fixedduedate", "paymmode", "paymspec", "paymentsched", "cashdisc", "cashdiscpercent", "paymdayid"]}, {"groupId": "DiscMarkup", "label": "axFormLayout.salestable.groups.tabheaderpricecalc.discmarkup", "order": 3, "fields": ["multilinedisc", "enddisc", "discpercent", "linedisc", "markupgroup"]}], "fields": []}, {"tabId": "TabHeaderOther", "label": "axFormLayout.salestable.tabs.tabheaderother", "order": 9, "groups": [{"groupId": "References", "label": "axFormLayout.salestable.groups.tabheaderother.references", "order": 1, "fields": ["customerref", "returnitemnum", "palletsimple"]}, {"groupId": "HeaderForeignTrade", "label": "axFormLayout.salestable.groups.tabheaderother.headerforeigntrade", "order": 2, "fields": ["transactioncode", "port", "statprocid", "listcode"]}, {"groupId": "Others", "label": "axFormLayout.salestable.groups.tabheaderother.others", "order": 5, "fields": ["pndconfdirection", "confdirection", "daysreservlimit"]}, {"groupId": "CommonParameters_Group", "label": "axFormLayout.salestable.groups.tabheaderother.commonparameters_group", "order": 6, "fields": ["paletkinds", "maxweightpallet", "netgrossweightlabels", "proteccionentrematerialypalet"]}, {"groupId": "SheetParameters_Group", "label": "axFormLayout.salestable.groups.tabheaderother.sheetparameters_group", "order": 7, "fields": ["maxheight", "volteado", "ajustarsehojas"]}, {"groupId": "ReelParameters_Group", "label": "axFormLayout.salestable.groups.tabheaderother.reelparameters_group", "order": 8, "fields": ["diametromandril", "diametroexteriorhasta"]}], "fields": []}, {"tabId": "CMT_Comments", "label": "axFormLayout.salestable.tabs.cmt_comments", "order": 10, "groups": [{"groupId": "Observations", "label": "axFormLayout.salestable.groups.cmt_comments.observations", "order": 1, "fields": ["custobservations"]}], "fields": []}, {"tabId": "TabHeaderReferences", "label": "axFormLayout.salestable.tabs.tabheaderreferences", "order": 11, "groups": [{"groupId": "groupPurchaseTable", "label": "axFormLayout.salestable.groups.tabheaderreferences.grouppurchasetable", "order": 2, "fields": ["purchId"]}], "fields": []}, {"tabId": "TabLineSetup", "label": "axFormLayout.salestable.tabs.tablinesetup", "order": 15, "groups": [{"groupId": "SalesLineDeliveryGroup", "label": "axFormLayout.salestable.groups.tablinesetup.saleslinedeliverygroup", "order": 5, "fields": ["receiptdaterequested", "shippingdaterequested", "receiptdateconfirmed", "shippingdateconfirmed", "deliverydatecontrol"]}], "fields": []}];

  const baseColumns = [
    {
      key: "iSalesId",
      label: "salesOrders.id",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: true,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "salesId",
      label: "salesOrders.salesId",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: false,
      disabled: true,
      type: "text"    },
    {
      key: "custaccount",
      label: "salesOrders.customer",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: customersService,
      optionLabel: "name",
      optionValue: "accountNum",
      lookupSelectedLabelField: "accountNum",
      lookupDisplayFields: ["blocked", "accountNum", "name", "namealias", "phone"],
      lookupSearchFields: ["accountNum", "name", "namealias", "phone"],
      lookupSearchMode: "or",
      lookupSort: {"field": "id.accountNum", "direction": "asc"},
      lookupEnumFields: {"blocked": "CustVendorBlocked"},
      lookupValueLabels: {"blocked": {"0": "No", "1": "Factura", "2": "Todo"}}    },
    {
      key: "invoiceaccount",
      label: "salesOrders.invoiceaccount",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: true,
      editable: false,
      disabled: true,
      type: "text"    },
    {
      key: "salesname",
      label: "salesOrders.name",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliverystreet",
      label: "salesOrders.deliverystreet",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliverycity",
      label: "salesOrders.deliverycity",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliveryzipcode",
      label: "salesOrders.deliveryzipcode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliverycounty",
      label: "salesOrders.deliverycounty",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: countiesService,
      optionLabel: "name",
      optionValue: "countyid",
      lookupSearchMode: "or",
      lookupDependsOn: [{"localField": "deliverycountry", "remoteFilterField": "countryid"}, {"localField": "deliverystate", "remoteFilterField": "stateid"}]    },
    {
      key: "deliverystate",
      label: "salesOrders.deliverystate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: statesService,
      optionLabel: "name",
      optionValue: "stateid",
      lookupSearchMode: "or",
      lookupDependsOn: [{"localField": "deliverycountry", "remoteFilterField": "countryid"}]    },
    {
      key: "deliverycountry",
      label: "salesOrders.deliverycountry",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: countriesService,
      optionLabel: "name",
      optionValue: "countryid",
      lookupSearchMode: "or"    },
    {
      key: "taxgroup",
      label: "salesOrders.taxgroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: taxGroupHeadingService,
      optionLabel: "taxgroupname",
      optionValue: "taxgroup",
      lookupSearchMode: "or"    },
    {
      key: "salesgroup",
      label: "salesOrders.salesgroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: commissionSalesGroupsService,
      optionLabel: "name",
      optionValue: "groupid",
      lookupSearchMode: "or"    },
    {
      key: "pricegroupid",
      label: "salesOrders.pricegroupid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: true,
      editable: false,
      disabled: false,
      type: "select",
      relatedService: priceDiscGroupsService,
      optionLabel: "name",
      optionValue: "groupid",
      lookupSearchMode: "or",
      filterType: "number"    },
    {
      key: "linedisc",
      label: "salesOrders.linedisc",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "enddisc",
      label: "salesOrders.enddisc",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "idaxdatesales",
      label: "salesOrders.date",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "date",
      filterType: "date"    },
    {
      key: "salestype",
      label: "salesOrders.salestype",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      defaultValue: 3,
      filterType: "select",
      enumName: "SalesType",
      options: [{"value": 0, "label": "Diario", "labelKey": "axEnums.SalesType.0"}, {"value": 1, "label": "Presupuesto", "labelKey": "axEnums.SalesType.1"}, {"value": 2, "label": "Suscripción", "labelKey": "axEnums.SalesType.2"}, {"value": 3, "label": "Pedido de ventas", "labelKey": "axEnums.SalesType.3"}, {"value": 4, "label": "Artículo devuelto", "labelKey": "axEnums.SalesType.4"}, {"value": 5, "label": "Pedido marco", "labelKey": "axEnums.SalesType.5"}, {"value": 6, "label": "Artículos requeridos", "labelKey": "axEnums.SalesType.6"}, {"value": 7, "label": "Información no definida", "labelKey": "axEnums.SalesType.7"}]    },
    {
      key: "salesstatus",
      label: "salesOrders.status",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: false,
      showInFilter: true,
      editable: false,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "SalesStatus",
      options: [{"value": 0, "label": "None", "labelKey": "axEnums.SalesStatus.0"}, {"value": 1, "label": "Pedido abierto", "labelKey": "axEnums.SalesStatus.1"}, {"value": 2, "label": "Entregado", "labelKey": "axEnums.SalesStatus.2"}, {"value": 3, "label": "Facturado", "labelKey": "axEnums.SalesStatus.3"}, {"value": 4, "label": "Cancelado", "labelKey": "axEnums.SalesStatus.4"}]    },
    {
      key: "fechaprecio",
      label: "salesOrders.fechaprecio",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime",
      filterType: "date"    },
    {
      key: "obsinternas",
      label: "salesOrders.obsinternas",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "obsalbaran",
      label: "salesOrders.obsalbaran",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "obsfactura",
      label: "salesOrders.obsfactura",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "obsagente",
      label: "salesOrders.obsagente",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "obstransporte",
      label: "salesOrders.obstransporte",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "obsproforma",
      label: "salesOrders.obsproforma",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "idaxsalesstatus",
      label: "salesOrders.idaxsalesstatus",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "salespoolid",
      label: "salesOrders.salespoolid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "salesoriginid",
      label: "salesOrders.salesoriginid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "idaxpsaddrdelivery",
      label: "salesOrders.idaxpsaddrdelivery",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "idaxpsaddrinvoice",
      label: "salesOrders.idaxpsaddrinvoice",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "multilinedisc",
      label: "salesOrders.multilinedisc",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliveryname",
      label: "salesOrders.deliveryname",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "dlvmode",
      label: "salesOrders.dlvmode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: deliveryModesService,
      optionLabel: "txt",
      optionValue: "code",
      lookupSearchMode: "or"    },
    {
      key: "markupgroup",
      label: "salesOrders.markupgroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "paymmode",
      label: "salesOrders.paymmode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: customerPaymentModesService,
      optionLabel: "name",
      optionValue: "paymmode",
      lookupSearchMode: "or"    },
    {
      key: "paymentsched",
      label: "salesOrders.paymentsched",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: false,
      disabled: true,
      type: "select",
      relatedService: paymentSchedulesService,
      optionLabel: "description",
      optionValue: "name"    },
    {
      key: "idaxpsmodule",
      label: "salesOrders.idaxpsmodule",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "idaxpstotalpaid",
      label: "salesOrders.idaxpstotalpaid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "idaxpstotalpaidreal",
      label: "salesOrders.idaxpstotalpaidreal",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "idaxpstotalproducts",
      label: "salesOrders.idaxpstotalproducts",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "idaxpstotalproductswt",
      label: "salesOrders.idaxpstotalproductswt",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "idaxpsconversionrate",
      label: "salesOrders.idaxpsconversionrate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "idaxpsidcart",
      label: "salesOrders.idaxpsidcart",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "idaxpsidcurrency",
      label: "salesOrders.idaxpsidcurrency",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "idaxpsidlang",
      label: "salesOrders.idaxpsidlang",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "idaxpsidcarrier",
      label: "salesOrders.idaxpsidcarrier",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "contactpersonid",
      label: "salesOrders.contactpersonid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      relatedService: contactsService,
      optionLabel: "name",
      optionValue: "contactpersonid",
      lookupSearchMode: "or",
      lookupDependsOn: [{"localField": "custaccount", "remoteFilterField": "custaccount"}]    },
    {
      key: "idaxpackingslipid",
      label: "salesOrders.idaxpackingslipid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "lastinvoice",
      label: "salesOrders.lastinvoice",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "lastpackingslipdate",
      label: "salesOrders.lastpackingslipdate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime",
      filterType: "date"    },
    {
      key: "lastinvoicedate",
      label: "salesOrders.lastinvoicedate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime",
      filterType: "date"    },
    {
      key: "recId",
      label: "salesOrders.recId",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: true,
      editable: false,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "modifieddate",
      label: "salesOrders.modifieddate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "datetime",
      filterType: "date"    },
    {
      key: "dataAreaId",
      label: "salesOrders.dataAreaId",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: true,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "isyncstatus",
      label: "salesOrders.isyncstatus",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "deliverydate",
      label: "salesOrders.deliverydate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime",
      filterType: "date"    },
    {
      key: "deliveryaddress",
      label: "salesOrders.deliveryaddress",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: false,
      disabled: true,
      type: "textarea",
      rows: 5    },
    {
      key: "purchorderformnum",
      label: "salesOrders.purchorderformnum",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "salestaker",
      label: "salesOrders.salestaker",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "reservation",
      label: "salesOrders.reservation",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "ItemReservation",
      options: [{"value": 0, "label": "Manual", "labelKey": "axEnums.ItemReservation.0"}, {"value": 1, "label": "Automático", "labelKey": "axEnums.ItemReservation.1"}, {"value": 2, "label": "Expansión", "labelKey": "axEnums.ItemReservation.2"}]    },
    {
      key: "freightsliptype",
      label: "salesOrders.freightsliptype",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "FreightSlipType",
      options: [{"value": 0, "label": "None", "labelKey": "axEnums.FreightSlipType.0"}, {"value": 1, "label": "Porte", "labelKey": "axEnums.FreightSlipType.1"}]    },
    {
      key: "documentstatus",
      label: "salesOrders.documentstatus",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "DocumentStatus",
      options: [{"value": 0, "label": "None", "labelKey": "axEnums.DocumentStatus.0"}, {"value": 1, "label": "Presupuesto", "labelKey": "axEnums.DocumentStatus.1"}, {"value": 2, "label": "Pedido de compra", "labelKey": "axEnums.DocumentStatus.2"}, {"value": 3, "label": "Confirmación", "labelKey": "axEnums.DocumentStatus.3"}, {"value": 4, "label": "Preparación del pedido", "labelKey": "axEnums.DocumentStatus.4"}, {"value": 5, "label": "Albarán", "labelKey": "axEnums.DocumentStatus.5"}, {"value": 6, "label": "Lista de recepciones", "labelKey": "axEnums.DocumentStatus.6"}, {"value": 7, "label": "Factura", "labelKey": "axEnums.DocumentStatus.7"}, {"value": 8, "label": "Diario de aprobación de facturas", "labelKey": "axEnums.DocumentStatus.8"}, {"value": 9, "label": "Proyecto - Factura", "labelKey": "axEnums.DocumentStatus.9"}, {"value": 10, "label": "Proyecto - Albarán", "labelKey": "axEnums.DocumentStatus.10"}, {"value": 11, "label": "Presupuesto de CRM", "labelKey": "axEnums.DocumentStatus.11"}, {"value": 12, "label": "Perdido", "labelKey": "axEnums.DocumentStatus.12"}, {"value": 13, "label": "Cancelado", "labelKey": "axEnums.DocumentStatus.13"}]    },
    {
      key: "currencyCode",
      label: "salesOrders.currencyCode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "payment",
      label: "salesOrders.payment",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "cashdisc",
      label: "salesOrders.cashdisc",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "custgroup",
      label: "salesOrders.custgroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "discpercent",
      label: "salesOrders.discpercent",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "customerref",
      label: "salesOrders.customerref",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "listcode",
      label: "salesOrders.listcode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "Listcode",
      options: [{"value": 0, "label": "No incluido", "labelKey": "axEnums.Listcode.0"}, {"value": 1, "label": "Comercio en UE", "labelKey": "axEnums.Listcode.1"}, {"value": 2, "label": "Producción sobre la tasa", "labelKey": "axEnums.Listcode.2"}, {"value": 3, "label": "Comercio comunitario/triangular", "labelKey": "axEnums.Listcode.3"}, {"value": 4, "label": "Triangular/Producción sobre la tasa", "labelKey": "axEnums.Listcode.4"}]    },
    {
      key: "purchId",
      label: "salesOrders.purchId",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "postingprofile",
      label: "salesOrders.postingprofile",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "transactioncode",
      label: "salesOrders.transactioncode",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliverycountryregionid",
      label: "salesOrders.deliverycountryregionid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "onetimecustomer",
      label: "salesOrders.onetimecustomer",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "covstatus",
      label: "salesOrders.covstatus",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "commissiongroup",
      label: "salesOrders.commissiongroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: true,
      editable: false,
      disabled: false,
      type: "select",
      relatedService: commissionSalesGroupsService,
      optionLabel: "name",
      optionValue: "groupid",
      lookupSearchMode: "or"    },
    {
      key: "email",
      label: "salesOrders.email",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "email"    },
    {
      key: "freightzone",
      label: "salesOrders.freightzone",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "returnitemnum",
      label: "salesOrders.returnitemnum",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "cashdiscpercent",
      label: "salesOrders.cashdiscpercent",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "deadline",
      label: "salesOrders.deadline",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime",
      filterType: "date"    },
    {
      key: "projid",
      label: "salesOrders.projid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "inventlocationid",
      label: "salesOrders.inventlocationid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "addressreftableid",
      label: "salesOrders.addressreftableid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "vatnum",
      label: "salesOrders.vatnum",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "port",
      label: "salesOrders.port",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "incltax",
      label: "salesOrders.incltax",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "numbersequencegroup",
      label: "salesOrders.numbersequencegroup",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "fixedexchrate",
      label: "salesOrders.fixedexchrate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "languageid",
      label: "salesOrders.languageid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "girotype",
      label: "salesOrders.girotype",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "PaymentStub",
      options: [{"value": 0, "label": "Ninguno", "labelKey": "axEnums.PaymentStub.0"}, {"value": 1, "label": "FIK 65", "labelKey": "axEnums.PaymentStub.1"}, {"value": 2, "label": "BBS", "labelKey": "axEnums.PaymentStub.2"}, {"value": 3, "label": "Resguardo azul", "labelKey": "axEnums.PaymentStub.3"}, {"value": 4, "label": "Resguardo rojo", "labelKey": "axEnums.PaymentStub.4"}, {"value": 5, "label": "FIK 762", "labelKey": "axEnums.PaymentStub.5"}, {"value": 6, "label": "Resguardo naranja", "labelKey": "axEnums.PaymentStub.6"}, {"value": 7, "label": "S101", "labelKey": "axEnums.PaymentStub.7"}, {"value": 8, "label": "S102", "labelKey": "axEnums.PaymentStub.8"}, {"value": 9, "label": "Finlandés", "labelKey": "axEnums.PaymentStub.9"}]    },
    {
      key: "printmoduletype",
      label: "salesOrders.printmoduletype",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "ModuleCustSalesParameters",
      options: [{"value": 0, "label": "Cliente", "labelKey": "axEnums.ModuleCustSalesParameters.0"}, {"value": 1, "label": "Pedido de ventas", "labelKey": "axEnums.ModuleCustSalesParameters.1"}, {"value": 2, "label": "Parámetros", "labelKey": "axEnums.ModuleCustSalesParameters.2"}, {"value": 3, "label": "Presupuesto", "labelKey": "axEnums.ModuleCustSalesParameters.3"}, {"value": 4, "label": "Usuario", "labelKey": "axEnums.ModuleCustSalesParameters.4"}]    },
    {
      key: "paymspec",
      label: "salesOrders.paymspec",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "fixedduedate",
      label: "salesOrders.fixedduedate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime",
      filterType: "date"    },
    {
      key: "exportreason",
      label: "salesOrders.exportreason",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "statprocid",
      label: "salesOrders.statprocid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "dlvreason",
      label: "salesOrders.dlvreason",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "quotationid",
      label: "salesOrders.quotationid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "deliverydatecontrol",
      label: "salesOrders.deliverydatecontrol",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "receiptdaterequested",
      label: "salesOrders.receiptdaterequested",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime",
      filterType: "date"    },
    {
      key: "receiptdateconfirmed",
      label: "salesOrders.receiptdateconfirmed",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime",
      filterType: "date"    },
    {
      key: "shippingdaterequested",
      label: "salesOrders.shippingdaterequested",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime",
      filterType: "date"    },
    {
      key: "shippingdateconfirmed",
      label: "salesOrders.shippingdateconfirmed",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime",
      filterType: "date"    },
    {
      key: "salesresponsible",
      label: "salesOrders.salesresponsible",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "salesunitid",
      label: "salesOrders.salesunitid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "addressrefrecid",
      label: "salesOrders.addressrefrecid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "ajustarsehojas",
      label: "salesOrders.ajustarsehojas",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "albaranvalued",
      label: "salesOrders.albaranvalued",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "confdirection",
      label: "salesOrders.confdirection",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "custobservations",
      label: "salesOrders.custobservations",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: false,
      editable: true,
      disabled: false,
      type: "textarea"    },
    {
      key: "daysreservlimit",
      label: "salesOrders.daysreservlimit",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "deliveryphone",
      label: "salesOrders.deliveryphone",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "phone"    },
    {
      key: "diametroexteriorhasta",
      label: "salesOrders.diametroexteriorhasta",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "diametromandril",
      label: "salesOrders.diametromandril",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "directdebitmandate",
      label: "salesOrders.directdebitmandate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "estimate",
      label: "salesOrders.estimate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "invoicingpfp",
      label: "salesOrders.invoicingpfp",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "invoicingtypeid",
      label: "salesOrders.invoicingtypeid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "maxheight",
      label: "salesOrders.maxheight",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "maxweightpallet",
      label: "salesOrders.maxweightpallet",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "netgrossweightlabels",
      label: "salesOrders.netgrossweightlabels",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "paletkinds",
      label: "salesOrders.paletkinds",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "PaletKinds",
      options: [{"value": 0, "label": "None", "labelKey": "axEnums.PaletKinds.0"}, {"value": 1, "label": "NONStop (S)", "labelKey": "axEnums.PaletKinds.1"}, {"value": 2, "label": "Doble", "labelKey": "axEnums.PaletKinds.2"}, {"value": 3, "label": "Triple", "labelKey": "axEnums.PaletKinds.3"}, {"value": 4, "label": "Cuadruple", "labelKey": "axEnums.PaletKinds.4"}, {"value": 5, "label": "NONStop", "labelKey": "axEnums.PaletKinds.5"}, {"value": 6, "label": "L NON Stop", "labelKey": "axEnums.PaletKinds.6"}]    },
    {
      key: "palletsimple",
      label: "salesOrders.palletsimple",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "paymdayid",
      label: "salesOrders.paymdayid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "pndconfdirection",
      label: "salesOrders.pndconfdirection",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "printlinedetails",
      label: "salesOrders.printlinedetails",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "proteccionentrematerialypalet",
      label: "salesOrders.proteccionentrematerialypalet",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "warehousetimecalendar",
      label: "salesOrders.warehousetimecalendar",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "cmturgencykind",
      label: "salesOrders.cmturgencykind",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "UrgencyKind",
      options: [{"value": 0, "label": "None", "labelKey": "axEnums.UrgencyKind.0"}, {"value": 1, "label": "Primera hora", "labelKey": "axEnums.UrgencyKind.1"}, {"value": 3, "label": "Recoge cliente", "labelKey": "axEnums.UrgencyKind.3"}, {"value": 2, "label": "Agencia", "labelKey": "axEnums.UrgencyKind.2"}, {"value": 4, "label": "Planif tarde", "labelKey": "axEnums.UrgencyKind.4"}, {"value": 5, "label": "Destino Comart", "labelKey": "axEnums.UrgencyKind.5"}, {"value": 6, "label": "Material en depósito", "labelKey": "axEnums.UrgencyKind.6"}, {"value": 7, "label": "Extranjero", "labelKey": "axEnums.UrgencyKind.7"}, {"value": 8, "label": "Proforma", "labelKey": "axEnums.UrgencyKind.8"}, {"value": 9, "label": "Destino Sumapel", "labelKey": "axEnums.UrgencyKind.9"}, {"value": 10, "label": "Destino Besalu", "labelKey": "axEnums.UrgencyKind.10"}, {"value": 11, "label": "Sólo mañanas", "labelKey": "axEnums.UrgencyKind.11"}, {"value": 12, "label": "Consultas ventas", "labelKey": "axEnums.UrgencyKind.12"}, {"value": 13, "label": "Entregas parciales", "labelKey": "axEnums.UrgencyKind.13"}, {"value": 14, "label": "Entrega directa fábrica", "labelKey": "axEnums.UrgencyKind.14"}, {"value": 15, "label": "Requiere Orden Manipulado", "labelKey": "axEnums.UrgencyKind.15"}, {"value": 16, "label": "RESERVA POR CONSIGNA", "labelKey": "axEnums.UrgencyKind.16"}, {"value": 17, "label": "RESERVA POR PROGRAMACIONES", "labelKey": "axEnums.UrgencyKind.17"}, {"value": 18, "label": "Se compra cortado entrega desde Comart", "labelKey": "axEnums.UrgencyKind.18"}, {"value": 19, "label": "Recoge comercial", "labelKey": "axEnums.UrgencyKind.19"}, {"value": 20, "label": "Contabilidad", "labelKey": "axEnums.UrgencyKind.20"}, {"value": 21, "label": "Stock de Cornellà", "labelKey": "axEnums.UrgencyKind.21"}, {"value": 22, "label": "Reposición por reclamación", "labelKey": "axEnums.UrgencyKind.22"}, {"value": 23, "label": "Agencia entrega mismo día", "labelKey": "axEnums.UrgencyKind.23"}, {"value": 24, "label": "Nueva entrada máquina", "labelKey": "axEnums.UrgencyKind.24"}, {"value": 25, "label": "Pendiente de compras", "labelKey": "axEnums.UrgencyKind.25"}, {"value": 26, "label": "Destino manipulador", "labelKey": "axEnums.UrgencyKind.26"}, {"value": 27, "label": "Pendiente cliente", "labelKey": "axEnums.UrgencyKind.27"}, {"value": 28, "label": "Multientregas", "labelKey": "axEnums.UrgencyKind.28"}, {"value": 29, "label": "Falta conforme", "labelKey": "axEnums.UrgencyKind.29"}, {"value": 30, "label": "Recibido cobro Proforma", "labelKey": "axEnums.UrgencyKind.30"}]    },
    {
      key: "requesteddelivdate",
      label: "salesOrders.requesteddelivdate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime",
      filterType: "date"    },
    {
      key: "requesteddelivdateorig",
      label: "salesOrders.requesteddelivdateorig",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "datetime",
      filterType: "date"    },
    {
      key: "sourcefilehash",
      label: "salesOrders.sourcefilehash",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text"    },
    {
      key: "sourcefolderpath",
      label: "salesOrders.sourcefolderpath",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text"    },
    {
      key: "sourcefilename",
      label: "salesOrders.sourcefilename",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text"    },
    {
      key: "sourceemailfilename",
      label: "salesOrders.sourceemailfilename",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "email"    },
    {
      key: "sourcejsonfilename",
      label: "salesOrders.sourcejsonfilename",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: true,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text"    },
    {
      key: "volteado",
      label: "salesOrders.volteado",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      defaultValue: 0,
      filterType: "select",
      enumName: "NoYes",
      options: [{"value": 0, "label": "No", "labelKey": "axEnums.NoYes.0"}, {"value": 1, "label": "Sí", "labelKey": "axEnums.NoYes.1"}]    },
    {
      key: "modifiedtime",
      label: "salesOrders.modifiedtime",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "modifiedby",
      label: "salesOrders.modifiedby",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "createddate",
      label: "salesOrders.createddate",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "datetime",
      filterType: "date"    },
    {
      key: "createdtime",
      label: "salesOrders.createdtime",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "number",
      filterType: "number"    },
    {
      key: "createdby",
      label: "salesOrders.createdby",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "idaxCreatedBy",
      label: "salesOrders.idaxCreatedBy",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "idaxModifiedBy",
      label: "salesOrders.idaxModifiedBy",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: false,
      type: "text"    },
    {
      key: "cmtseleccionbobinasestadopv",
      label: "salesOrders.cmtseleccionbobinasestadopv",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "select",
      filterType: "select",
      enumName: "CMTSeleccionBobinasEstadoPV",
      options: [{"value": 0, "label": "No enviado", "labelKey": "axEnums.CMTSeleccionBobinasEstadoPV.0"}, {"value": 1, "label": "Enviado", "labelKey": "axEnums.CMTSeleccionBobinasEstadoPV.1"}, {"value": 2, "label": "Revisado", "labelKey": "axEnums.CMTSeleccionBobinasEstadoPV.2"}, {"value": 3, "label": "Reenviado", "labelKey": "axEnums.CMTSeleccionBobinasEstadoPV.3"}]    },
    {
      key: "cmtseleccionbobinasenviadopor",
      label: "salesOrders.cmtseleccionbobinasenviadopor",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: true,
      editable: true,
      disabled: false,
      type: "text"    },
    {
      key: "idaxhasdirectionreport",
      label: "salesOrders.idaxhasdirectionreport",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: false,
      editable: true,
      disabled: false,
      type: "checkbox",
      filterType: "checkbox"    },
    {
      key: "idaxhasmanipulationorder",
      label: "salesOrders.idaxhasmanipulationorder",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: true,
      showInForm: true,
      showInFilter: false,
      editable: true,
      disabled: false,
      type: "checkbox",
      filterType: "checkbox"    },
    {
      key: "salesLine.lineNum",
      label: "salesOrderLines.lineNum",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "number",
      backendKey: "salesLine.lineNum",
      table: "salesLine",
      tableLabel: "salesLines.title",
      sourceJoin: "salesLine",
      filterConfigurable: true,
      filterType: "number"    },
    {
      key: "salesLine.itemId",
      label: "salesOrderLines.itemId",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text",
      backendKey: "salesLine.itemId",
      table: "salesLine",
      tableLabel: "salesLines.title",
      sourceJoin: "salesLine",
      filterConfigurable: true    },
    {
      key: "salesLine.name",
      label: "salesOrderLines.name",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text",
      backendKey: "salesLine.name",
      table: "salesLine",
      tableLabel: "salesLines.title",
      sourceJoin: "salesLine",
      filterConfigurable: true    },
    {
      key: "salesLine.externalitemid",
      label: "salesOrderLines.externalitemid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text",
      backendKey: "salesLine.externalitemid",
      table: "salesLine",
      tableLabel: "salesLines.title",
      sourceJoin: "salesLine",
      filterConfigurable: true    },
    {
      key: "salesLine.typeitemid",
      label: "salesOrderLines.typeitemid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text",
      backendKey: "salesLine.typeitemid",
      table: "salesLine",
      tableLabel: "salesLines.title",
      sourceJoin: "salesLine",
      filterConfigurable: true    },
    {
      key: "salesLine.gramajeitemid",
      label: "salesOrderLines.gramajeitemid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text",
      backendKey: "salesLine.gramajeitemid",
      table: "salesLine",
      tableLabel: "salesLines.title",
      sourceJoin: "salesLine",
      filterConfigurable: true    },
    {
      key: "salesLine.venditemid",
      label: "salesOrderLines.venditemid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text",
      backendKey: "salesLine.venditemid",
      table: "salesLine",
      tableLabel: "salesLines.title",
      sourceJoin: "salesLine",
      filterConfigurable: true    },
    {
      key: "salesLine.salesQty",
      label: "salesOrderLines.salesQty",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "number",
      backendKey: "salesLine.salesQty",
      table: "salesLine",
      tableLabel: "salesLines.title",
      sourceJoin: "salesLine",
      filterConfigurable: true,
      filterType: "number"    },
    {
      key: "salesLine.qtyOrdered",
      label: "salesOrderLines.qtyOrdered",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "number",
      backendKey: "salesLine.qtyOrdered",
      table: "salesLine",
      tableLabel: "salesLines.title",
      sourceJoin: "salesLine",
      filterConfigurable: true,
      filterType: "number"    },
    {
      key: "salesLine.salesunit",
      label: "salesOrderLines.salesunit",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text",
      backendKey: "salesLine.salesunit",
      table: "salesLine",
      tableLabel: "salesLines.title",
      sourceJoin: "salesLine",
      filterConfigurable: true    },
    {
      key: "salesLine.salesPrice",
      label: "salesOrderLines.salesPrice",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "number",
      backendKey: "salesLine.salesPrice",
      table: "salesLine",
      tableLabel: "salesLines.title",
      sourceJoin: "salesLine",
      filterConfigurable: true,
      filterType: "number"    },
    {
      key: "salesLine.lineamount",
      label: "salesOrderLines.lineamount",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "number",
      backendKey: "salesLine.lineamount",
      table: "salesLine",
      tableLabel: "salesLines.title",
      sourceJoin: "salesLine",
      filterConfigurable: true,
      filterType: "number"    },
    {
      key: "salesLine.width",
      label: "salesOrderLines.width",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "number",
      backendKey: "salesLine.width",
      table: "salesLine",
      tableLabel: "salesLines.title",
      sourceJoin: "salesLine",
      filterConfigurable: true,
      filterType: "number"    },
    {
      key: "salesLine.depth",
      label: "salesOrderLines.depth",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "number",
      backendKey: "salesLine.depth",
      table: "salesLine",
      tableLabel: "salesLines.title",
      sourceJoin: "salesLine",
      filterConfigurable: true,
      filterType: "number"    },
    {
      key: "salesLine.inventtransid",
      label: "salesOrderLines.inventtransid",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "text",
      backendKey: "salesLine.inventtransid",
      table: "salesLine",
      tableLabel: "salesLines.title",
      sourceJoin: "salesLine",
      filterConfigurable: true    },
    {
      key: "salesLine.confirmeddlv",
      label: "salesOrderLines.confirmeddlv",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "datetime",
      backendKey: "salesLine.confirmeddlv",
      table: "salesLine",
      tableLabel: "salesLines.title",
      sourceJoin: "salesLine",
      filterConfigurable: true,
      filterType: "date"    },
    {
      key: "salesLine.receiptdateconfirmed",
      label: "salesOrderLines.receiptdateconfirmed",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "datetime",
      backendKey: "salesLine.receiptdateconfirmed",
      table: "salesLine",
      tableLabel: "salesLines.title",
      sourceJoin: "salesLine",
      filterConfigurable: true,
      filterType: "date"    },
    {
      key: "salesLine.shippingdateconfirmed",
      label: "salesOrderLines.shippingdateconfirmed",
      fieldPermissionControlled: true,
      sortable: true,
      showInGrid: false,
      showInForm: false,
      showInFilter: false,
      editable: false,
      disabled: true,
      type: "datetime",
      backendKey: "salesLine.shippingdateconfirmed",
      table: "salesLine",
      tableLabel: "salesLines.title",
      sourceJoin: "salesLine",
      filterConfigurable: true,
      filterType: "date"    },
  ];

  const columns = transformColumns ? transformColumns(baseColumns) : baseColumns;
  const initialFormData = columns.reduce((acc, col) => {
    if (
      col &&
      col.showInForm &&
      Object.prototype.hasOwnProperty.call(col, "defaultValue")
    ) {
      acc[col.key] = col.defaultValue;
    }
    return acc;
  }, {});

  const defaultCrudConfig = {
    canCreate: true,
    canEdit: true,
    canDelete: false
  };

  const crudConfig = {
    ...defaultCrudConfig,
    gridRowStyleResolver: defaultGridRowStyleResolver,
    ...(crudConfigOverride || {})
  };

  return (
    <CrudManager
      service={service}
      columns={columns}
      entityName="salesOrders"
      permissionPrefix="legacy.salesOrders"
      icon={faFileInvoice}
      formLayout="page"
      canCreate={crudConfig.canCreate}
      canEdit={crudConfig.canEdit}
      canDelete={crudConfig.canDelete}
      initialFilters={crudConfig.initialFilters || {}}
      initialSort={crudConfig.initialSort || null}
      gridRowStyleResolver={crudConfig.gridRowStyleResolver || null}
      joinRowGroupingDefaults={JOIN_ROW_GROUPING_DEFAULTS}
      renderForm={crudConfig.renderForm || null}
      modalClassName={crudConfig.modalClassName || ""}
      formTabs={formTabs}
      initialFormData={initialFormData}
    />
  );
};

export default SalesOrdersPageGen;